import React from 'react';
import styled from 'styled-components';

const Card = styled.div`
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0.45rem 0.65rem;
`;

const CardTitle = styled.h5`
  font-weight: 600;
  color: #495057;
  margin-bottom: 0;
  font-size: 1.1rem;
`;

const CardBody = styled.div`
  padding: 0.65rem;
`;

const Row = styled.div`
  display: flex;
  gap: 0.65rem;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Col = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h6`
  font-size: 1rem;
  font-weight: 600;
  margin: 0.75rem 0 0.5rem;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  padding-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin: 1.25rem 0 0.75rem;
  }
`;

const GuideTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #dee2e6;
  background-color: white;
`;

const TableHeader = styled.th`
  background-color: #6c757d;
  color: white;
  font-weight: 600;
  padding: 0.45rem;
  text-align: center;
  border: 1px solid #dee2e6;
`;

const TableCell = styled.td`
  padding: 0.45rem;
  text-align: center;
  background-color: white;
  border: 1px solid #dee2e6;

  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.85rem;
  }
`;

const TableRow = styled.tr`
  &:nth-child(even) td {
    background-color: #f8f9fa;
  }
`;

const Alert = styled.div`
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  padding: 0.6rem;
  margin-top: 0.6rem;
  margin-bottom: 0;
  color: #0c5460;
`;

interface GuideProps {
  contractType: string;
}

const Guide: React.FC<GuideProps> = ({ contractType }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <i className="bi bi-book me-2"></i>Trading Probability Guide
        </CardTitle>
      </CardHeader>
      <CardBody>
        <Row>
          <Col>
            <SectionTitle>Over Probabilities</SectionTitle>
            <GuideTable>
              <thead>
                <tr>
                  <TableHeader>1</TableHeader>
                  <TableHeader>2</TableHeader>
                  <TableHeader>3</TableHeader>
                  <TableHeader>4</TableHeader>
                </tr>
              </thead>
              <tbody>
                <TableRow>
                  <TableCell>90%</TableCell>
                  <TableCell>85%</TableCell>
                  <TableCell>70%</TableCell>
                  <TableCell>60%</TableCell>
                </TableRow>
                <TableRow>
                  <TableHeader>5</TableHeader>
                  <TableHeader>6</TableHeader>
                  <TableHeader>7</TableHeader>
                  <TableHeader>8</TableHeader>
                </TableRow>
                <TableRow>
                  <TableCell>55%</TableCell>
                  <TableCell>40%</TableCell>
                  <TableCell>30%</TableCell>
                  <TableCell>25%</TableCell>
                </TableRow>
              </tbody>
            </GuideTable>
          </Col>
          <Col>
            <SectionTitle>Under Probabilities</SectionTitle>
            <GuideTable>
              <thead>
                <tr>
                  <TableHeader>1</TableHeader>
                  <TableHeader>2</TableHeader>
                  <TableHeader>3</TableHeader>
                  <TableHeader>4</TableHeader>
                </tr>
              </thead>
              <tbody>
                <TableRow>
                  <TableCell>25%</TableCell>
                  <TableCell>30%</TableCell>
                  <TableCell>40%</TableCell>
                  <TableCell>55%</TableCell>
                </TableRow>
                <TableRow>
                  <TableHeader>5</TableHeader>
                  <TableHeader>6</TableHeader>
                  <TableHeader>7</TableHeader>
                  <TableHeader>8</TableHeader>
                </TableRow>
                <TableRow>
                  <TableCell>60%</TableCell>
                  <TableCell>70%</TableCell>
                  <TableCell>85%</TableCell>
                  <TableCell>90%</TableCell>
                </TableRow>
              </tbody>
            </GuideTable>
          </Col>
        </Row>

        <SectionTitle>Signal Strength Guide</SectionTitle>
        <GuideTable>
          <thead>
            <tr>
              <TableHeader>Pattern</TableHeader>
              <TableHeader>Even/Odd</TableHeader>
              <TableHeader>Rise/Fall</TableHeader>
            </tr>
          </thead>
          <tbody>
            <TableRow>
              <TableCell>Strong Signal</TableCell>
              <TableCell>Above 65%</TableCell>
              <TableCell>Above 65%</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Moderate Signal</TableCell>
              <TableCell>55-65%</TableCell>
              <TableCell>55-65%</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Weak Signal</TableCell>
              <TableCell>Below 55%</TableCell>
              <TableCell>Below 55%</TableCell>
            </TableRow>
          </tbody>
        </GuideTable>

        <Alert>
          <i className="bi bi-info-circle me-2"></i>
          <strong>Tip:</strong> Combine pattern analysis with probability indicators for better trade decisions.
        </Alert>
      </CardBody>
    </Card>
  );
};

export default Guide;
